import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Navbar from "../components/Navbar";
import CategoryPayment from "../components/CategoryPayment";
import "./css/CommonComplete.css";

import onboardingCompletedImage from "../assets/images/success.png";

export default function ProfileStep5() {
  const navigate = useNavigate();
  const [initialLoading, setInitialLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [showOnboardingCompleted, setShowOnboardingCompleted] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");
  const [initialSubCategories, setInitialSubCategories] = useState([]);
  const [initialSpecialisations, setInitialSpecialisations] = useState([]);
  const [initialWorkAmount, setInitialWorkAmount] = useState(null);
  const [disabledSubCategories, setDisabledSubCategories] = useState([]);
  const [disabledSpecialisations, setDisabledSpecialisations] = useState([]);

  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;

  // Translations
  const t = {
    en: {
      loading: "Loading",
      step: "Step",
      categorySelection: "Category Selection",
      subtitle: "Please select the categories that you work in.",
      onboardingCompleted: "Onboarding Completed",
      onboardingMessage: "We have received your payment of AED {amount}, you can start using the app now.",
      continue: "Continue",
      failedToLoad: "Failed to load profile data",
      somethingWrong: "Something went wrong!",
    },
    ar: {
      loading: "جاري التحميل",
      step: "الخطوة",
      categorySelection: "اختيار الفئة",
      subtitle: "يرجى اختيار الفئات التي تعمل فيها.",
      onboardingCompleted: "اكتمل التسجيل",
      onboardingMessage: "لقد استلمنا دفعتك البالغة {amount} درهم، يمكنك الآن البدء في استخدام التطبيق.",
      continue: "متابعة",
      failedToLoad: "فشل تحميل بيانات الملف الشخصي",
      somethingWrong: "حدث خطأ ما!",
    },
  }[lang];

  // Fetch user profile data on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setInitialLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profileData = data.response;

          // Set language preference from API response to localStorage
          if (profileData.language_preference) {
            localStorage.setItem("app_language", profileData.language_preference);
            setLang(profileData.language_preference);
          }

          setProfile(profileData);

          // Navigation logic based on profile_completed_step
          const completedStep = profileData.profile_completed_step || 0;
          
          // If onboarding is complete, navigate to dashboard
          if (completedStep === 5) {
            navigate("/dashboard");
            return;
          }

          // Check if user can access this step
          // Must have profile_completed_step >= 3 and is_verified === true
          if (
            !completedStep ||
            completedStep < 3 ||
            profileData.is_verified !== true
          ) {
            // Redirect to the appropriate step
            const redirectStep = completedStep ? completedStep + 1 : 1;
            navigate(`/profile-step-${redirectStep}`);
            return;
          }

          // If user has already purchased sub_categories, load them
          if (
            profileData.sub_categories &&
            Array.isArray(profileData.sub_categories) &&
            profileData.sub_categories.length > 0
          ) {
            // Convert ObjectId references to string array
            const subCatIds = profileData.sub_categories.map((sc) =>
              typeof sc === "object" && sc._id ? sc._id : sc.toString()
            );
            setInitialSubCategories(subCatIds);
            setDisabledSubCategories(subCatIds); // Disable already purchased categories
          }

          // If user has already purchased specialisations, load them
          if (
            profileData.specialisations &&
            Array.isArray(profileData.specialisations) &&
            profileData.specialisations.length > 0
          ) {
            const specIds = profileData.specialisations.map((sp) =>
              typeof sp === "object" && sp._id ? sp._id : sp.toString()
            );
            setInitialSpecialisations(specIds);
            setDisabledSpecialisations(specIds);
          }

          // If user has work_amount, load it
          if (profileData.work_amount) {
            setInitialWorkAmount({
              min: profileData.work_amount.min || 0,
              max: profileData.work_amount.max || 0,
            });
          }
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        if (err.code !== 404) {
          const currentLang = localStorage.getItem("app_language") || "en";
          const translations = {
            en: { failedToLoad: "Failed to load profile data" },
            ar: { failedToLoad: "فشل تحميل بيانات الملف الشخصي" },
          };
          showToast(err.message || translations[currentLang].failedToLoad, "error");
        }
      } finally {
        setInitialLoading(false);
      }
    };

    fetchProfile();
  }, [USER_BASE_URL, VERSION, navigate]);

  // Handle payment success
  const handlePaymentSuccess = async () => {
    // Reload profile to check if step is now 5
    try {
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/profile/`,
        "GET",
        null,
        {}
      );

      if (data && data.response_code === 200 && data.response) {
        const profileData = data.response;
        setProfile(profileData);

        if (profileData.profile_completed_step === 5) {
          setShowOnboardingCompleted(true);
        } else {
          // Reload the page to show updated data
          window.location.reload();
        }
      }
    } catch (err) {
      console.error("Error fetching profile after payment:", err);
      // Reload anyway
      window.location.reload();
    }
  };

  // Handle continue from onboarding completed
  const handleContinue = () => {
    navigate("/dashboard");
  };

  // Loading screen
  if (initialLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }

  // Onboarding completed screen
  if (showOnboardingCompleted) {
    // Get payment amount from profile if available
    let paymentAmount = "200"; // Default
    if (profile && profile.last_payment_amount) {
      paymentAmount = profile.last_payment_amount.toString();
    }

    return (
      <div className="common-wrapper" dir={lang === "ar" ? "rtl" : "ltr"}>
        <Navbar
          is_step_heading={true}
          step_number={5}
          heading={t.step}
          is_back_arrow={false}
        />
        <div className="verification-status-container">
          <h2 className="title">{t.onboardingCompleted}</h2>
          <p className="subtitle">
            {t.onboardingMessage.replace("{amount}", paymentAmount)}
          </p>
          <div className="verification-image-container">
            <img
              src={onboardingCompletedImage}
              alt="Onboarding Completed"
              className="verification-image"
            />
          </div>
        </div>
        <button onClick={handleContinue} className="continue-btn">
            {t.continue}
        </button>
      </div>
    );
  }

  return (
    <div className="common-wrapper" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Navbar
        is_step_heading={true}
        step_number={5}
        heading={t.step}
        is_back_arrow={true}
        back_route="/profile-step-4"
      />
      <div className="common-content">
        <h1 className="title">{t.categorySelection}</h1>
        <p className="subtitle">{t.subtitle}</p>
      </div>
      <CategoryPayment
          onPaymentSuccess={handlePaymentSuccess}
          initialSubCategories={initialSubCategories}
          initialSpecialisations={initialSpecialisations}
          initialWorkAmount={initialWorkAmount}
          disabledSubCategories={disabledSubCategories}
          disabledSpecialisations={disabledSpecialisations}
          lang={lang}
        />
    </div>
  );
}
